'use client'

import { useContext, useEffect } from 'react'
import { useOurStore } from '@/app/_providers/StoreProvider'
import { useCart } from '@/app/_providers/CartProvider/CartProvider'
import { useSeller } from '@/app/_providers/SellerProvider/SellerProvider'
import LoginContext from '@/app/_providers/LoginProvider/LoginContext'
import StepContext from '@/app/_providers/StepProvider/StepContext'
import useExperimentVariant from '@/hooks/useExperimentVariant'
import { PORTAL_PASS_INTERRUPT_EXPERIMENT, PORTAL_PASS_INTERRUPT_VARIANTS } from '@/config/experiments.config'
import { StepId } from '@/config/navigation.config'
import {
  getPortalPassEligibilityMessage,
  getPortalPassIneligibilityReasons,
} from '@/utils/components/order-tickets.utils'
import { getTotals } from '@/utils/cart.utils'
import { logger } from '@/utils/logger'
import TXT from '@/messages'

const { log } = logger()

/** The banner lives on the ticket step, then rides along to add-ons, and stops there. */
const TICKET_STEP_ID: StepId = 'ticket-type'
const ADD_ONS_STEP_ID: StepId = 'add-ons'

/**
 * Which arm of the Portal Pass interrupt A/B test the user is in.
 *
 * Both the banner and the interrupt modal need this answer, and both are mounted on the
 * ticket step at once. `useExperimentVariant` caches per experiment, so calling this
 * from several components still costs a single Amplitude fetch and a single exposure.
 *
 * `isEligible` must describe an audience that cannot depend on the arm the user lands
 * in, or the experiment measures its own gate. Callers who are not eligible are never
 * bucketed, and read as the control arm.
 *
 * `isExposed` is the moment either arm becomes live: the banner arm would show the
 * banner now. It is the same moment for both arms, so the control arm is counted at
 * the point where it *would* have seen a banner, and the comparison stays fair.
 *
 * @param {boolean} isEligible - Whether this user could be shown the promo at all.
 * @param {boolean} isExposed - Whether the user has reached the experiment's exposure point.
 * @returns {boolean} True when the user is in the `banner` arm.
 */
export const usePortalPassInterruptVariant = (isEligible: boolean, isExposed: boolean): boolean => (
  useExperimentVariant(PORTAL_PASS_INTERRUPT_EXPERIMENT, { enabled: isEligible, exposed: isExposed })
  === PORTAL_PASS_INTERRUPT_VARIANTS.banner
)

export type PortalPassBannerState = {
  /** True when the user is in the banner arm of the A/B test (the modal arm is the default). */
  isBannerVariant: boolean
  /** True when the banner should be rendered right now. */
  showBanner: boolean
  /** Full copy, shown before the banner minimizes. */
  body: string
  /** Condensed copy, shown once the banner has minimized. */
  bodyMinimized: string
  /** True past the ticket step, where the banner has said its piece and gets out of the way. */
  minimized: boolean
  /** Portal Pass destination the banner links to. */
  url: string
}

/**
 * Resolves everything the Portal Pass banner needs: which arm of the A/B test the
 * user is in, whether the banner is eligible to show, and the copy to show.
 *
 * `isBannerVariant` is returned separately from `showBanner` because callers need
 * both answers for different reasons — `OrderTickets` suppresses the blocking
 * interrupt modal for the whole banner arm, not just while the banner is visible.
 *
 * @returns {PortalPassBannerState} The banner's variant, visibility and copy.
 */
const usePortalPassBanner = (): PortalPassBannerState => {
  const seller = useSeller()
  const { user } = useContext(LoginContext)
  const { isCurrentStepEqualTo } = useContext(StepContext)
  const admissionType = useOurStore((state) => state.admissionType)
  const localLineItems = useOurStore((state) => state.localLineItems)
  const lineItemsToDelete = useOurStore((state) => state.lineItemsToDelete)
  const { cart } = useCart()

  const { interruptModal: { url, bannerBody, bannerBodyMinimized }, portalPassDayPassEnabled } = seller
  const { interruptModals: { portalPass } } = TXT

  // Walked once and shared: the verdict and the explanation are the same decision, so
  // deriving both from one pass keeps them from ever disagreeing.
  const ineligibilityReasons = getPortalPassIneligibilityReasons({
    user,
    portalPassDayPassEnabled,
    admissionType,
    portalPassUrl: url,
  })

  const isAudienceEligible = ineligibilityReasons.length === 0

  const { localLineItemsTotal, cartLineItemsTotal, lineItemsToDeleteTotal } = getTotals({
    localLineItems,
    cartLineItems: cart.lineItems,
    lineItemsToDelete,
  })
  // The upsell only makes sense once the user has committed to a ticket, so it waits for
  // the first increment — staged locally on the ticket step, already in the cart after it.
  const hasTickets = localLineItemsTotal + cartLineItemsTotal - lineItemsToDeleteTotal > 0

  const isTicketStep = isCurrentStepEqualTo(TICKET_STEP_ID)

  // Everything the banner needs to show, except being in the banner arm.
  const isAtExposurePoint = isAudienceEligible
    && hasTickets
    && (isTicketStep || isCurrentStepEqualTo(ADD_ONS_STEP_ID))

  // Explain the audience decision in non-production consoles. Keyed on the verdict
  // rather than the render, so it reports changes instead of repeating itself.
  const eligibilityMessage = getPortalPassEligibilityMessage(ineligibilityReasons)
  useEffect(() => log(eligibilityMessage), [eligibilityMessage])

  // Audience gates the experiment, not the other way round: a user who could never see a
  // banner must not be bucketed and counted against it. Deliberately *not* gated on the
  // step or the cart — those come and go as the user moves, and un-bucketing a user
  // mid-flow would flip them back to the control arm.
  const isBannerVariant = usePortalPassInterruptVariant(isAudienceEligible, isAtExposurePoint)

  return {
    isBannerVariant,
    showBanner: isBannerVariant && isAtExposurePoint,
    body: bannerBody || portalPass.bannerBody,
    bodyMinimized: bannerBodyMinimized || portalPass.bannerBodyMinimized,
    minimized: !isTicketStep,
    // `isAudienceEligible` already guarantees a valid URL wherever `showBanner` is true.
    url: url ?? '#',
  }
}

export default usePortalPassBanner
